import React, { useEffect, useState } from "react";
import { IoDocumentTextOutline, IoSearch } from "react-icons/io5";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { motion } from "framer-motion";
import Pagination from "@mui/material/Pagination";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import useAuth from "../../useAuth";
import { toast } from "react-toastify";
import { server } from "../../server";
import { useDebounce } from "../../hooks/useDebounce";
import { projectStatusList } from "../../jsons/projectStatusList";
import LoadingComponent from "../../Components/LoadingComponent";

export default function ProjectsList() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [projectsList, setProjectsList] = useState([]);
  const [projectsCount, setProjectsCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState("");
  const [projectType, setProjectType] = useState("");

  const debounceValue = useDebounce(searchParams, 500);
  useEffect(() => {
    getAllProjects();
  }, [pageNo, pageSize, debounceValue]);

  const getAllProjects = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/project/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          pageNo,
          pageSize,
          searchParams,
          projectType,
          deleted: false,
          userType: user?.userType === "partner" ? "partner" : null,
        },
      });
      setProjectsList(data?.data);
      setProjectsCount(data?.count);
      setLoading(false);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
      setLoading(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    const userRes = await window.confirm(
      "Are you sure, you want to delete this project"
    );
    if (!userRes) return;

    try {
      setLoading(true);
      const { data } = await axios.delete(`${server}/project/deleteById`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          projectId,
        },
      });
      getAllProjects();
      setLoading(false);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-6">
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="max-w-6xl mx-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <IoDocumentTextOutline className="text-3xl text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-800">Projects List</h1>
          </div>

          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search projects..."
              value={searchParams}
              onChange={(e) => setSearchParams(e.target.value)}
              className="pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-300 focus:outline-none"
            />
            <IoSearch className="absolute left-3 top-2.5 text-gray-400" />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-white rounded-xl shadow border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="text-left p-3 font-semibold text-gray-600">#</th>
                <th className="text-left p-3 font-semibold text-gray-600">
                  Project Type
                </th>
                <th className="text-left p-3 font-semibold text-gray-600">
                  Project Name
                </th>
                <th className="text-left p-3 font-semibold text-gray-600">
                  Description
                </th>
                <th className="text-left p-3 font-semibold text-gray-600">
                  Start Date
                </th>
                <th className="text-center p-3 font-semibold text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {projectsList.map((project, index) => (
                <tr
                  key={project.id}
                  className="border-b hover:bg-gray-50 transition cursor-pointer"
                  onClick={() =>
                    navigate("../projectDetails", {
                      state: { projectData: project },
                    })
                  }
                >
                  <td className="p-3 text-gray-700">
                    {(pageNo - 1) * pageSize + index + 1}
                  </td>
                  <td className="p-3 text-gray-800 font-medium">
                    {project.projectType}
                  </td>
                  <td className="p-3 text-gray-800 font-medium">
                    {project.projectName}
                  </td>
                  <td className="p-3 text-gray-600">{project.description}</td>
                  <td className="p-3 text-gray-500">
                    {
                      projectStatusList?.find((e) => e?.slug === project.status)
                        ?.label
                    }
                  </td>
                  <td
                    className="p-3 text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex gap-3 justify-center">
                      <button className="p-2 rounded-full hover:bg-blue-100 text-blue-600 transition">
                        <FiEdit className="text-lg" />
                      </button>
                      <button
                        className="p-2 rounded-full hover:bg-red-100 text-red-600 transition"
                        onClick={() => handleDeleteProject(project?.id)}
                      >
                        <FiTrash2 className="text-lg" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {projectsCount.length === 0 && (
                <tr>
                  <td colSpan="5" className="p-5 text-center text-gray-500">
                    No projects found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-end mt-6">
          <Pagination
            count={Math.ceil(projectsCount / pageSize)}
            page={pageNo}
            onChange={(event, value) => setPageNo(value)}
            color="primary"
            shape="rounded"
          />
        </div>
      </motion.div>
      {loading && <LoadingComponent />}
    </div>
  );
}
